import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { GlobalService } from "src/app/shared/services";
import {
  Firestore,
  collection,
  query,
  orderBy,
  collectionData,
} from "@angular/fire/firestore";
import { Platform } from "@ionic/angular";
import { EventColors } from "src/app/models/event-colors";
import { Subscription } from "rxjs";
import { UserDataService } from "src/app/shared/services/user-data/user-data.service";
import { EventDataService } from "src/app/shared/services/eventData/event-data.service";
import { ModuleDataService } from "src/app/shared/services/module-data/module-data.service";
import { DaoAttendeesService } from "src/app/providers/db/dao-attendees.service";

@Component({
  selector: "app-ranking",
  templateUrl: "./ranking.page.html",
  styleUrls: ["./ranking.page.scss"],
})
export class RankingPage implements OnInit, OnDestroy {
  isLoading: boolean = true;
  module = null;
  moduleId: string = null;
  event = null;
  eventId: string = null;
  eventColors: EventColors = new EventColors();
  isMobile: boolean = false;
  subscriptions: Subscription[] = [];
  language: string;
  mergeInteractivityPoints: boolean = false;
  showOnlyAttendeeWithPoints: boolean = false;
  attendees = [];
  attendeesToShow = [];
  searchBarQuery: string = "";
  filteredAttendees = [];

  constructor(
    private route: ActivatedRoute,
    public global: GlobalService,
    private firestore: Firestore,
    private SUserData: UserDataService,
    private SEventData: EventDataService,
    private platform: Platform,
    private moduleDataService: ModuleDataService, 
	private SAttendees: DaoAttendeesService,
  ) {
    const params = this.route.snapshot.params;
    this.eventId = params.eventId;
    this.moduleId = params.moduleId;

    this.language = this.SEventData.getLanguage();
    this.loadColors();
  }

  /**
   * @description get event colors
   * @param eventId
   */
  loadColors(eventId: string = this.eventId) {
    const subscription = this.SEventData.getEventDataListener(
      eventId
    ).subscribe((eventData) => {
      this.eventColors = eventData.colors;
      document.body.style.setProperty(
        `--background-clr`,
        this.eventColors.menu_color
      );
      document.body.style.setProperty(
        `--myUser-color`,
        this.eventColors.menu_text_color
      );
    });
    this.subscriptions.push(subscription);
  }

  ngOnInit() {
    if (this.platform.is("ios") || this.platform.is("android")) {
      this.isMobile = true;
    }

    this.fetchAttendees();
    this.getModule();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe;
    });
  }

  getModule() {
    this.subscriptions.push(
      this.moduleDataService
        .getSingleModuleListener(this.moduleId, this.eventId)
        .subscribe((module) => {
          this.module = module;
          this.mergeInteractivityPoints = module.mergeInteractivityPoints
            ? module.mergeInteractivityPoints
            : false;
          this.showOnlyAttendeeWithPoints = module.showOnlyAttendeeWithPoints
            ? module.showOnlyAttendeeWithPoints
            : false;

          this.handleAttendeesToShow();
        })
    );
  }

  fetchAttendees() {
	this.subscriptions.push(
		this.SAttendees.fetchAttendeesByEvent(this.eventId)
		.subscribe((attendees) =>{
			this.attendees = attendees ?? [];
			this.handleAttendeesToShow();
		})
	)
  }

  handleAttendeesToShow() {
	this.isLoading = true;
    this.attendeesToShow = [];
    for (let i = 0; i < this.attendees.length; i++) {
      let attendeePoints = this.getAttendeeEngagementPoints(this.attendees[i]);

      if (this.mergeInteractivityPoints) {
        attendeePoints += this.attendees[i].points;
      }

      if (this.showOnlyAttendeeWithPoints) {
        if (attendeePoints > 0) {
          this.attendeesToShow.push({ 
            ...this.attendees[i], 
            points: attendeePoints,
          });
        }
      } else {
        this.attendeesToShow.push({ 
          ...this.attendees[i], 
          points: attendeePoints,
        });
      }
    }

    this.attendeesToShow = this.sortAttendeesByPoints(this.attendeesToShow);
    this.isLoading = false;
  }

  resetSearchBar() {
    this.filteredAttendees = [];
    this.searchBarQuery = "";
  }

  searchBar(query: string) {
    query = query.trim();
    if (query) {
      this.filteredAttendees = this.attendeesToShow.filter((attendee) => {
        return attendee.name.toLowerCase().includes(query.toLowerCase());
      });
    }

    this.searchBarQuery = query;
  }

  sortAttendeesByPoints(attendees: any[]) {
    if (!attendees || attendees.length <= 0) return [];
    return attendees.sort((a, b) => a.points > b.points ? -1 : 1).map((attendee, index) => {
      attendee.position = index + 1;
      return attendee;
    });
  }

  getAttendeeEngagementPoints(attendee) {
    if (!attendee.engagementPoints) return 0;
    return Object.values<number>(attendee.engagementPoints).reduce(
      (acc: number, cur: number) => acc + cur,
      0
    );
  }
}
